import React, { Component, Suspense } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Button, ButtonGroup, Card, Tabs, Tab } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popconfirm } from "antd";

import UserTypes from "components/auth/user-types";
import { closeCustomModal, openCustomModal } from "components/custom/modals";
import {
	fetchInterviewRounds,
	clearInterviewRounds,
	deleteInterviewRound,
	generateInterviewRound,
	evaluateInterviewRound
} from "api/menu/hr/interviews/actions";
import { promoteInterviewQuestion } from "api/menu/hr/interview-questions/actions";
import { fetchInterviewCategories } from "api/menu/hr/interview-categories/actions";
import { normalizeInterviewLabel } from "utils/normalize-interview-label";

const InterviewRoundModal = React.lazy(() => import("./interview-round-modal"));

const getRoundQuestions = (round) => round.questions || round.answers || [];

const isCompleted = (round) => round.status === "Completed";

const formatUtc = (value) => (value ? new Date(value).toLocaleString() : null);

class InterviewSection extends Component {
	constructor(props) {
		super(props);
		this.state = { promoteAnswerId: null, promoteCategoryId: "", activeRoundId: null };
	}

	componentDidMount() {
		this.props.fetchInterviewRounds(this.props.applicantId, false);
		if (this.canPromote()) {
			this.props.fetchInterviewCategories(false);
		}
	}

	componentWillUnmount() {
		this.props.clearInterviewRounds();
	}

	componentDidUpdate(prevProps) {
		const rounds = this.props.rounds || [];
		const prevRounds = prevProps.rounds || [];
		if (rounds === prevRounds) {
			return;
		}

		if (rounds.length === 0) {
			if (this.state.activeRoundId != null) {
				this.setState({ activeRoundId: null });
			}
			return;
		}

		const roundIds = rounds.map(r => r.id);
		if (this.state.activeRoundId == null || !roundIds.includes(this.state.activeRoundId)) {
			const latest = [...rounds].sort((a, b) => b.sequence - a.sequence)[0];
			this.setState({ activeRoundId: latest.id });
		}
	}

	handleTabSelect = (key) => {
		if (key != null) {
			this.setState({ activeRoundId: parseInt(key, 10) });
		}
	};

	getRoundTabTitle = (round) => {
		const label = round.label ? `: ${round.label}` : "";
		const status = isCompleted(round) ? "Completed" : "Draft";
		return `Round ${round.sequence}${label} (${status})`;
	};

	refresh = () => {
		this.props.fetchInterviewRounds(this.props.applicantId, false);
	};

	notifyApplicantUpdated = () => {
		if (this.props.onApplicantRefresh) {
			this.props.onApplicantRefresh();
		}
	};

	getResumeFilename = () => {
		const a = this.props.applicant;
		if (!a || !a.resumeFileId) return null;
		return a.resumeFileName || a.fileName || a.filename || a.originalFileName || `resume-${a.resumeFileId}.pdf`;
	};

	openRoundModal = (roundId = false, readOnly = false) => {
		const isCreate = roundId === false;
		const resumeFileId = this.props.applicant ? this.props.applicant.resumeFileId || null : null;
		const modalTitle = isCreate ? "Create" : (readOnly ? "View" : "Edit");
		const modalObj = {
			id: `interview-round-${roundId || "new"}`,
			header: `${modalTitle} Interview Round`,
			modalSettings: {
				size: "lg",
				width: "min(900px, calc(100vw - 16px))",
				bodyStyle: {
					maxHeight: "calc(100vh - 8rem)",
					overflowY: "auto",
					WebkitOverflowScrolling: "touch"
				}
			},
			children: (
				<Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
					<InterviewRoundModal
						applicantId={this.props.applicantId}
						roundId={roundId}
						readOnly={readOnly}
						resumeFileId={resumeFileId}
						resumeFilename={this.getResumeFilename()}
						onCancel={() => this.props.closeCustomModal(modalObj)}
						onSaved={() => {
							this.refresh();
							this.notifyApplicantUpdated();
							this.props.closeCustomModal(modalObj);
						}}
					/>
				</Suspense>
			),
			handleClose: () => this.props.closeCustomModal(modalObj)
		};
		this.props.openCustomModal(modalObj);
	};

	generateRound = () => {
		this.props.generateInterviewRound(this.props.applicantId, {}, (round) => {
			if (round && round.id) {
				this.setState({ activeRoundId: round.id });
			}
			this.refresh();
			this.notifyApplicantUpdated();
		});
	};

	deleteRound = (roundId) => {
		this.props.deleteInterviewRound(this.props.applicantId, roundId, () => {
			this.setState({ activeRoundId: null });
			this.refresh();
		});
	};

	evaluateRound = (roundId) => {
		this.props.evaluateInterviewRound(this.props.applicantId, roundId, () => {
			this.refresh();
			this.notifyApplicantUpdated();
		});
	};

	promoteAnswer = () => {
		const answerId = parseInt(this.state.promoteAnswerId, 10);
		const categoryId = parseInt(this.state.promoteCategoryId, 10);
		if (!answerId || !categoryId) return;
		this.props.promoteInterviewQuestion({ answerId, categoryId }, () => {
			this.setState({ promoteAnswerId: null, promoteCategoryId: "" });
			this.refresh();
		});
	};

	findCategoryIdForSnapshot = (categorySnapshot) => {
		if (!categorySnapshot || !this.props.categories) {
			return "";
		}
		const normalized = normalizeInterviewLabel(categorySnapshot).toLowerCase();
		const match = this.props.categories.find(
			c => normalizeInterviewLabel(c.name).toLowerCase() === normalized
		);
		return match ? String(match.id) : "";
	};

	startPromote = (answer) => {
		this.setState({
			promoteAnswerId: answer.id,
			promoteCategoryId: this.findCategoryIdForSnapshot(answer.categorySnapshot)
		});
	};

	hasParsedResume = () => !!(this.props.applicant && this.props.applicant.parsedJson);

	canUseAi = () => this.hasParsedResume() && !this.props.jobPostingNeedsAttention;

	canPromote = () => {
		const role = this.props.auth && this.props.auth.user ? this.props.auth.user.role : [];
		if (role.includes(UserTypes.supervisor) || role.includes(UserTypes.nationalSupervisor)) {
			return false;
		}
		return role.includes(UserTypes.admin) || role.includes(UserTypes.hr);
	};

	canPromoteAnswer = (answer) =>
		this.canPromote() && answer.isCustom && !answer.questionId && answer.id;

	canEvaluate = (round) => {
		if (!this.canUseAi() || !isCompleted(round)) return false;
		if (!round.interviewerConclusion) return false;
		const questions = getRoundQuestions(round);
		return questions.some(q => q.answerText && q.answerText.trim());
	};

	evaluateDisabledReason = (round) => {
		if (this.props.jobPostingNeedsAttention) {
			return "Job posting needs attention: no description set. AI features are disabled.";
		}
		if (!this.hasParsedResume()) {
			return "Requires parsed resume, interviewer conclusion, and at least one answer";
		}
		if (!round.interviewerConclusion) {
			return "Requires an interviewer conclusion";
		}
		return "Requires at least one answered question";
	};

	renderPreparationPanel = (round) => {
		if (round.createdVia !== "AI" || isCompleted(round) || !round.preparationResultJson) {
			return null;
		}
		try {
			const obj = JSON.parse(round.preparationResultJson);
			const prep = obj.preparation || {};
			const suggested = obj.suggestedQuestions || [];

			return (
				<div className="mt-3 p-3 bg-light border rounded">
					<strong>AI Preparation Guidance</strong>
					{Array.isArray(prep.focusAreas) && prep.focusAreas.length > 0 && (
						<div className="mt-2">
							<strong>Focus areas:</strong>
							<ul className="mb-0 pl-3">
								{prep.focusAreas.map((item, i) => <li key={i}>{normalizeInterviewLabel(item)}</li>)}
							</ul>
						</div>
					)}
					{prep.interviewerGuidance && (
						<div className="mt-2">
							<strong>Interviewer guidance:</strong>
							<p className="mb-0 mt-1" style={{ whiteSpace: "pre-wrap" }}>{normalizeInterviewLabel(prep.interviewerGuidance)}</p>
						</div>
					)}
					{Array.isArray(prep.riskFlags) && prep.riskFlags.length > 0 && (
						<div className="mt-2">
							<strong>Risk flags:</strong>
							<ul className="mb-0 pl-3">
								{prep.riskFlags.map((item, i) => <li key={i}>{normalizeInterviewLabel(item)}</li>)}
							</ul>
						</div>
					)}
					{suggested.length > 0 && (
						<div className="mt-3">
							<strong>Suggested questions</strong>
							<div className="table-responsive mt-1">
								<table className="table table-sm table-striped mb-0">
									<thead>
										<tr>
											<th>Question</th>
											<th>Category</th>
											<th>Priority</th>
											<th>Rationale</th>
										</tr>
									</thead>
									<tbody>
										{suggested.map((sq, i) => (
											<tr key={i}>
												<td>{normalizeInterviewLabel(sq.questionText)}</td>
												<td>{normalizeInterviewLabel(sq.category)}</td>
												<td>{sq.priority}</td>
												<td>{normalizeInterviewLabel(sq.rationale)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</div>
			);
		} catch (e) {
			return <div className="mt-3 text-danger">Error parsing AI preparation guidance</div>;
		}
	};

	renderAiResult = (round) => {
		if (!round.resultJson) {
			return <div className="text-muted">No post-interview AI assessment yet.</div>;
		}
		try {
			const obj = JSON.parse(round.resultJson);
			const scoring = obj.postInterviewScoring || {};
			const overall = scoring.overall || {};
			const summary = obj.summary || {};
			const perQuestion = scoring.perQuestion || [];

			return (
				<div>
					<div className="mb-2">
						<strong>Post-Interview Score:</strong> {round.postInterviewScore != null ? round.postInterviewScore : overall.score}
						{round.recommendation && <> &nbsp;|&nbsp; <strong>Recommendation:</strong> {round.recommendation}</>}
						{round.evaluatedUtc && <> &nbsp;|&nbsp; <strong>Evaluated:</strong> {formatUtc(round.evaluatedUtc)}</>}
					</div>
					{summary.text && <p className="mb-2">{summary.text}</p>}
					{Array.isArray(summary.strengths) && summary.strengths.length > 0 && (
						<div className="mb-2"><strong>Strengths:</strong> {summary.strengths.join("; ")}</div>
					)}
					{Array.isArray(summary.concerns) && summary.concerns.length > 0 && (
						<div className="mb-2"><strong>Concerns:</strong> {summary.concerns.join("; ")}</div>
					)}
					{perQuestion.length > 0 && (
						<div className="table-responsive">
							<table className="table table-sm table-striped mb-0">
								<thead>
									<tr>
										<th>Question</th>
										<th>Category</th>
										<th>Assessment</th>
										<th>Impact</th>
									</tr>
								</thead>
								<tbody>
									{perQuestion.map((pq, i) => (
										<tr key={i}>
											<td>{pq.questionText}</td>
											<td>{pq.category}</td>
											<td>{pq.assessment}</td>
											<td>{pq.scoreImpact}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			);
		} catch (e) {
			return <div className="text-danger">Error parsing AI result</div>;
		}
	};

	renderRoundPanel = (round) => {
		const completed = isCompleted(round);
		const questions = getRoundQuestions(round);

		return (
			<div>
				<div className="d-flex align-items-center flex-wrap mb-3 pb-2 border-bottom">
					<span className="text-muted small">
						{round.conductedByName ? `Conducted by ${round.conductedByName}` : null}
						{completed && round.completedUtc && (
							<> &nbsp;·&nbsp; Completed {formatUtc(round.completedUtc)}</>
						)}
						{round.createdVia === "AI" && !completed && (
							<> &nbsp;·&nbsp; AI-prepared</>
						)}
					</span>
					<ButtonGroup className="ml-auto">
						{!completed && (
							<Button size="sm" variant="outline-primary" onClick={() => this.openRoundModal(round.id)}>
								<FontAwesomeIcon icon="pen" className="mr-1" /> Edit
							</Button>
						)}
						{completed && (
							<Button
								size="sm"
								variant="outline-info"
								disabled={!this.canEvaluate(round) || this.props.evaluateLoading}
								onClick={() => this.evaluateRound(round.id)}
								title={!this.canEvaluate(round) ? this.evaluateDisabledReason(round) : undefined}
							>
								{this.props.evaluateLoading ? (
									<FontAwesomeIcon icon="spinner" spin className="mr-1" />
								) : (
									<FontAwesomeIcon icon="redo" className="mr-1" />
								)}
								Evaluate with AI
							</Button>
						)}
						<Popconfirm
							title="Delete this interview round?"
							onConfirm={() => this.deleteRound(round.id)}
							placement="bottomRight"
							okText="Yes"
							cancelText="Cancel"
						>
							<Button size="sm" variant="outline-danger">
								<FontAwesomeIcon icon="times" />
							</Button>
						</Popconfirm>
					</ButtonGroup>
				</div>
				{this.renderPreparationPanel(round)}
				<strong>Questions &amp; Answers</strong>
				{questions.length === 0 && (
					<div className="text-muted mt-2">No questions added yet.</div>
				)}
				{questions.map((a, i) => (
					<div key={i} className="mt-3 border-top pt-2">
						<div>
							<strong>{a.categorySnapshot ? `[${normalizeInterviewLabel(a.categorySnapshot)}] ` : ""}{a.questionTextSnapshot}</strong>
							{this.canPromoteAnswer(a) && (
								this.state.promoteAnswerId === a.id ? (
									<span className="ml-2">
										<select
											className="form-control form-control-sm d-inline-block"
											style={{ width: 180, display: "inline-block" }}
											value={this.state.promoteCategoryId}
											onChange={(e) => this.setState({ promoteCategoryId: e.target.value })}
										>
											<option value="">Category...</option>
											{(this.props.categories || []).map(c => (
												<option key={c.id} value={c.id}>{c.name}</option>
											))}
										</select>
										<Button size="sm" variant="link" className="p-0 ml-1" onClick={this.promoteAnswer}>Save</Button>
										<Button size="sm" variant="link" className="p-0 ml-1" onClick={() => this.setState({ promoteAnswerId: null, promoteCategoryId: "" })}>Cancel</Button>
									</span>
								) : (
									<Button
										size="sm"
										variant="link"
										className="ml-2 p-0"
										onClick={() => this.startPromote(a)}
									>
										Promote to bank
									</Button>
								)
							)}
						</div>
						<div className="text-muted mt-1" style={{ whiteSpace: "pre-wrap" }}>
							{a.answerText
								? a.answerText
								: (completed ? "—" : <em>No answer yet</em>)}
						</div>
					</div>
				))}
				{round.interviewerConclusion && (
					<div className="mt-4">
						<strong>Interviewer Conclusion</strong>
						<div className="text-muted mt-1" style={{ whiteSpace: "pre-wrap" }}>{round.interviewerConclusion}</div>
					</div>
				)}
				{completed && (
					<div className="mt-4">
						<strong>Post-Interview AI Assessment</strong>
						<div className="mt-2">{this.renderAiResult(round)}</div>
					</div>
				)}
			</div>
		);
	};

	render() {
		const { rounds, loading, generateLoading } = this.props;
		const canUseAi = this.canUseAi();
		const generateDisabledReason = this.props.jobPostingNeedsAttention
			? "Job posting needs attention: no description set. AI features are disabled."
			: "Requires parsed resume data";
		const sortedRounds = [...(rounds || [])].sort((a, b) => a.sequence - b.sequence);
		const activeKey = this.state.activeRoundId != null ? String(this.state.activeRoundId) : undefined;

		return (
			<div className="mt-3">
				<div className="d-flex align-items-center mb-3 flex-wrap">
					<h5 className="mb-0">Interview Rounds</h5>
					<div className="ml-auto">
						<Button
							className="mr-2"
							variant="primary"
							size="sm"
							onClick={() => this.openRoundModal()}
						>
							<FontAwesomeIcon icon="plus" className="mr-1" /> Create interview round manually
						</Button>
						<Button
							variant="info"
							size="sm"
							disabled={!canUseAi || generateLoading}
							onClick={this.generateRound}
							title={!canUseAi ? generateDisabledReason : undefined}
						>
							{generateLoading ? (
								<FontAwesomeIcon icon="spinner" spin className="mr-1" />
							) : (
								<FontAwesomeIcon icon="star" className="mr-1" />
							)}
							Create interview round using AI
						</Button>
					</div>
				</div>
				{loading && <div>Loading...</div>}
				{!loading && sortedRounds.length === 0 && (
					<div className="text-muted">No interview rounds recorded yet.</div>
				)}
				{!loading && sortedRounds.length > 0 && (
					<Tabs
						id="interview-rounds-tabs"
						activeKey={activeKey}
						onSelect={this.handleTabSelect}
						className="w-100"
					>
						{sortedRounds.map(round => (
							<Tab
								key={round.id}
								eventKey={String(round.id)}
								title={this.getRoundTabTitle(round)}
							>
								<Card className="border-top-0 rounded-0 rounded-bottom">
									<Card.Body>{this.renderRoundPanel(round)}</Card.Body>
								</Card>
							</Tab>
						))}
					</Tabs>
				)}
			</div>
		);
	}
}

InterviewSection.propTypes = {
	applicantId: PropTypes.number.isRequired,
	applicant: PropTypes.object,
	jobPostingNeedsAttention: PropTypes.bool,
	onApplicantRefresh: PropTypes.func,
	auth: PropTypes.object.isRequired,
	rounds: PropTypes.array.isRequired,
	categories: PropTypes.array,
	loading: PropTypes.bool.isRequired,
	generateLoading: PropTypes.bool.isRequired,
	evaluateLoading: PropTypes.bool.isRequired,
	fetchInterviewRounds: PropTypes.func.isRequired,
	clearInterviewRounds: PropTypes.func.isRequired,
	deleteInterviewRound: PropTypes.func.isRequired,
	generateInterviewRound: PropTypes.func.isRequired,
	evaluateInterviewRound: PropTypes.func.isRequired,
	promoteInterviewQuestion: PropTypes.func.isRequired,
	fetchInterviewCategories: PropTypes.func.isRequired,
	openCustomModal: PropTypes.func.isRequired,
	closeCustomModal: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
	rounds: state.interviews.rounds,
	categories: state.interviewCategories.categories,
	loading: state.interviews.loading,
	generateLoading: state.interviews.generateLoading,
	evaluateLoading: state.interviews.evaluateLoading,
	auth: state.auth
});

const mapDispatchToProps = dispatch => ({
	fetchInterviewRounds: bindActionCreators(fetchInterviewRounds, dispatch),
	clearInterviewRounds: bindActionCreators(clearInterviewRounds, dispatch),
	deleteInterviewRound: bindActionCreators(deleteInterviewRound, dispatch),
	generateInterviewRound: bindActionCreators(generateInterviewRound, dispatch),
	evaluateInterviewRound: bindActionCreators(evaluateInterviewRound, dispatch),
	promoteInterviewQuestion: bindActionCreators(promoteInterviewQuestion, dispatch),
	fetchInterviewCategories: bindActionCreators(fetchInterviewCategories, dispatch),
	openCustomModal: bindActionCreators(openCustomModal, dispatch),
	closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(InterviewSection);
